<template>
    <div
        v-if="displayItems.length"
        class="referral-context-menu__dropdown-menus referral-actions"
        :class="{ 'is-loading': isLoadingAction }"
    >
        <span v-if="availabilityMode && isLoadingAction" class="menu-item menu-item--loading">
            <span>{{ $t('pages.availability.index.actions.loading') }}</span>
        </span>

        <span
            v-for="(item, index) of displayItems"
            :key="index"
            class="referral-context-menu__menu-item"
            :class="{ 'disabled-item': item.disabled }"
            @click="!item.disabled && executeItem(item)"
        >
            <common-icon v-if="item.icon" :name="item.icon" size="small"></common-icon>

            <span>{{ item.label }}</span>
        </span>
    </div>
</template>

<script>
    import ReferralStatusMixin from 'mixins/ReferralStatusMixin'
    import UsersAPI from 'api/UsersAPI'
    import ReferralConfirmMixin from 'mixins/ReferralConfirmMixin'
    import cloneDeep from 'lodash.clonedeep'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import ReferralStatusEnum from '@/enums/ReferralStatusEnum'
    import ReferralPathTypeEnum from '@/enums/ReferralPathTypeEnum'
    import ReferralTrackingTypeEnum from '@/enums/ReferralTrackingTypeEnum'
    import StagesEnum from '@/enums/StagesEnum'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'

    export default {
        mixins: [ReferralStatusMixin, ReferralConfirmMixin, ScreenSizeDetectionMixin],

        props: {
            optionProp: { type: Object, required: true },
            asContextMenu: { type: Boolean, default: false },
            showCancelled: { type: Boolean, default: false },
            type: { type: String, required: true },
            availabilityMode: { type: Boolean, default: false },
        },

        data() {
            return {
                ReferralPathTypeEnum,
                option: {},
                isLoadingAction: false,
            }
        },

        computed: {
            isUnavailable() {
                return !!this.option.value.isUnavailable
            },

            referral() {
                return this.option.value.referral
            },

            referralId() {
                return (this.referral || {}).id || 0
            },

            isReferringPhysician() {
                return this.type === ReferralPathTypeEnum.MADE
            },

            allItems() {
                if (this.availabilityMode) {
                    return [
                        {
                            isValid: !this.isUnavailable,
                            label: this.$t('pages.availability.index.actions.set_unavailable'),
                            name: 'set-unavailable',
                            fn: this.setUnavailable,
                            icon: 'close',
                        },
                        {
                            isValid: this.isUnavailable,
                            label: this.$t('pages.availability.index.actions.set_available'),
                            name: 'set-available',
                            fn: this.setAvailable,
                            icon: 'check',
                        },
                        {
                            isValid: true,
                            label: this.$t('pages.availability.index.actions.create_referral'),
                            name: 'create-referral',
                            fn: this.createReferral,
                            disabled: this.isUnavailable,
                            icon: 'plus-circle',
                        },
                    ]
                }

                return [
                    {
                        isValid: this.asContextMenu && this.isTablet,
                        label: this.$t('pages.referrals.index.referrals_made.detail'),
                        name: 'open-details',
                        icon: 'eye',
                    },
                    {
                        isValid:
                            !this.asContextMenu &&
                            (this.isReferralOk(this.referral) || this.isReferralPending(this.referral)) &&
                            !this.availabilityMode &&
                            this.type !== 'availability',
                        label: this.referralStatusDescription,
                        name: 'status',
                        class: 'plain',
                        icon: null,
                    },
                    {
                        isValid:
                            !this.asContextMenu &&
                            !!this.referral &&
                            !(this.isReferralCancelledOrNoShow(this.referral) && !this.showCancelled),
                        label: this.$t(`pages.referrals.new.dropdown.label.view_referral`),
                        name: 'view',
                        route: this.getRoute(),
                        icon: 'eye',
                    },
                    {
                        isValid: this.asContextMenu && this.shouldShowReferralDetails(this.referral),
                        label: this.$t(`pages.referrals.new.dropdown.label.print_prescription`),
                        name: 'downloadPdf',
                        icon: 'download',
                    },
                    {
                        isValid: this.asContextMenu && this.shouldChooseSlot(this.referral),
                        label: this.$t(`pages.referrals.new.dropdown.label.referring.choose_slot`),
                        name: 'choose-slot',
                        icon: 'check',
                    },
                    {
                        isValid: this.asContextMenu && this.shouldUploadRequestedRecords(this.referral),
                        label: this.$t('pages.referrals.new.dropdown.label.referring.upload_requested_records'),
                        name: 'upload-additional-records',
                        icon: 'document',
                    },
                    {
                        isValid: this.asContextMenu && this.shouldProposeSlot(this.referral),
                        label: this.$t(`pages.referrals.new.dropdown.label.referred.propose_slot`),
                        name: 'propose-slot',
                        icon: 'calendar',
                    },
                    {
                        isValid: this.shouldExternalDecline(this.referral),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.decline'),
                        name: 'external-decline',
                        icon: 'close',
                    },
                    {
                        isValid: this.shouldExternalDecline(this.referral),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.additional_records'),
                        name: 'request-additional-records',
                        icon: 'attach',
                    },
                    {
                        isValid: this.asContextMenu && this.isReferralReschedulable(this.referral),
                        label: this.$t(`pages.referrals.new.dropdown.label.follow_up_visit`),
                        name: 'follow-up-visit',
                        icon: 'calendar',
                    },
                    {
                        isValid:
                            !this.isReferringPhysician &&
                            this.isReferralPending(this.referral) &&
                            this.referral.sourceType === ReferralTrackingTypeEnum.DIRECT &&
                            !this.isAppointmentInPast(this.referral),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.confirm'),
                        name: 'confirm',
                        route: this.getRoute('confirm'),
                        icon: 'check',
                    },
                    {
                        isValid:
                            !this.isReferringPhysician &&
                            this.referral &&
                            this.referral.sourceType === ReferralTrackingTypeEnum.DIRECT &&
                            (this.isReferralPending(this.referral) ||
                                (this.isReferralToBeRescheduled(this.referral) && !this.isReferralConfirmed(this.referral))),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.decline'),
                        name: 'decline',
                        route: this.getRoute('decline'),
                        icon: 'close',
                    },
                    {
                        isValid:
                            !this.isReferringPhysician &&
                            this.referral &&
                            this.referral.sourceType === ReferralTrackingTypeEnum.DIRECT &&
                            ((this.isReferralWaitingForPatient(this.referral) && !this.isAppointmentInPast(this.referral)) ||
                                this.isReferralWaitingForInsurance(this.referral) ||
                                this.isReferralPending(this.referral) ||
                                this.isReferralToBeRescheduled(this.referral) ||
                                this.isReferralPendingReschedule(this.referral)),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.reschedule'),
                        name: 'reschedule',
                        route: this.getRoute('reschedule'),
                        icon: 'calendar',
                    },
                    {
                        isValid:
                            !this.isReferringPhysician &&
                            this.referral &&
                            this.referral.sourceType === ReferralTrackingTypeEnum.DIRECT &&
                            ((this.isReferralWaitingForPatient(this.referral) && !this.isAppointmentInPast(this.referral)) ||
                                this.isReferralWaitingForInsurance(this.referral) ||
                                (this.isReferralToBeRescheduled(this.referral) && this.isReferralConfirmed(this.referral))),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.cancel'),
                        name: 'cancel',
                        route: this.getRoute('cancel'),
                        icon: 'close',
                    },
                    {
                        isValid:
                            !this.isReferringPhysician &&
                            this.referral &&
                            this.referral.sourceType === ReferralTrackingTypeEnum.DIRECT &&
                            (this.isReferralWaitingForReport(this.referral) || this.isReferralCompleted(this.referral)),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.add_report'),
                        name: 'addReport',
                        route: { name: `referrals.received.add-report`, params: { id: this.referralId } },
                        icon: 'file',
                    },
                    {
                        isValid:
                            this.isReferringPhysician &&
                            this.referral &&
                            this.referral.sourceType === ReferralTrackingTypeEnum.MATCH &&
                            this.isReferralWaitingForReport(this.referral),
                        label: this.$t('pages.referrals.new.dropdown.label.referring.request_report'),
                        name: 'requestReport',
                        icon: 'file',
                    },
                    {
                        isValid:
                            this.isReferringPhysician &&
                            this.referral &&
                            !this.isReferralCancelledOrNoShow(this.referral) &&
                            ((this.referral.sourceType === ReferralTrackingTypeEnum.DIRECT &&
                                !this.isReferralWaitingForReport(this.referral)) ||
                                (this.referral.sourceType === ReferralTrackingTypeEnum.MATCH &&
                                    this.referral.status !== ReferralStatusEnum.SCHEDULED)),
                        label: this.$t('pages.referrals.new.dropdown.label.referring.cancel'),
                        name: 'cancel',
                        route: this.getRoute('cancel'),
                        icon: 'close',
                    },
                    {
                        isValid:
                            this.asContextMenu &&
                            !this.isReferringPhysician &&
                            this.referral &&
                            this.referral.sourceType === ReferralTrackingTypeEnum.DIRECT &&
                            this.isAppointmentInPast(this.referral) &&
                            (this.isReferralWaitingForPatient(this.referral) || this.isReferralCancelledOrNoShow(this.referral)),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.patient_seen'),
                        name: 'patientSeen',
                        route: this.getRoute(),
                        icon: 'check',
                    },
                    {
                        isValid: this.isReferringPhysician && this.isReferralError(this.referral),
                        label: this.$t('pages.referrals.new.dropdown.label.referring.match'),
                        name: 'match',
                        icon: 'link',
                    },
                    {
                        isValid:
                            !this.asContextMenu &&
                            (!this.referral || (this.isReferralCancelledOrNoShow(this.referral) && !this.showCancelled)) &&
                            !this.isUnavailable,
                        label: this.$t(`pages.referrals.new.dropdown.label.set_unavailable`),
                        fn: this.setUnavailable,
                        icon: 'close',
                    },
                    {
                        isValid:
                            !this.asContextMenu &&
                            (this.$auth.isPracticePremium() || this.$auth.isPracticeTrial()) &&
                            (!this.referral || (this.isReferralCancelledOrNoShow(this.referral) && !this.showCancelled)),
                        label: this.$t(`pages.referrals.new.dropdown.label.create_referral`),
                        fn: this.createReferral,
                        disabled: this.isUnavailable,
                        icon: 'plus-circle',
                    },
                    {
                        isValid: !this.asContextMenu && !this.referral && this.isUnavailable,
                        label: this.$t(`pages.referrals.new.dropdown.label.set_available`),
                        fn: this.setAvailable,
                        icon: 'check',
                    },
                    {
                        isValid:
                            !this.asContextMenu &&
                            !!this.referral &&
                            !(this.isReferralCancelledOrNoShow(this.referral) && !this.showCancelled),
                        label: this.$t(`pages.referrals.new.dropdown.label.patient_visit`),
                        name: 'patient-profile',
                        fn: this.goToPatientProfile,
                        icon: 'user',
                    },
                ]
            },

            displayItems() {
                return this.allItems.filter((item) => item.isValid)
            },

            referralStatusDescription() {
                if (!this.referral) return ''
                return this.$t(`pages.referrals.tracking.status_description.${this.referral.status}`)
            },

            slotData() {
                return {
                    userId: this.$auth.getActiveMember().id,
                    date: this.option.value.date,
                    startTime: this.option.value.startTime,
                    endTime: this.option.value.endTime,
                    practiceId: this.$auth.getPractice().id,
                    practiceLocationId: Number(this.$route.query.locationId) || 0,
                }
            },
        },

        methods: {
            goToPatientProfile() {
                this.$router.push(`/patients/details/patients/${this.referral.patientId}`)
            },
            mapSourceTypeToUrl(sourceType) {
                if (
                    sourceType === ReferralTrackingTypeEnum.DIRECT ||
                    sourceType === 'referral' ||
                    sourceType === 'referrals' ||
                    sourceType === 'direct'
                ) {
                    return 'direct'
                }
                if (sourceType === ReferralTrackingTypeEnum.MATCH || sourceType === 'referrals_pool' || sourceType === 'match') {
                    return 'match'
                }
                return 'direct'
            },
            getRoute(action, actionAs = 'query') {
                const type = this.type === ReferralPathTypeEnum.MADE || this.type === 'made' ? 'made' : 'received'
                const sourceType = this.mapSourceTypeToUrl(this.referral?.sourceType)
                const id = this.referralId || this.referral?.id

                const route = {
                    name: `referrals.${type}`,
                    params: {
                        type,
                        sourceType,
                        id,
                    },
                }
                if (action) {
                    route[actionAs] = { action: action }
                }
                return route
            },

            executeItem(item) {
                if (this.availabilityMode) {
                    if (item.disabled || this.isLoadingAction) {
                        return
                    }
                    if (item.name === 'create-referral') {
                        item.fn && item.fn()
                        this.emitEvent({ name: 'closeReferralContextMenu' })
                        return
                    }

                    this.isLoadingAction = true
                    this.emitEvent({
                        name: 'availability-action-start',
                        payload: {
                            slotData: this.slotData,
                            action: item.name,
                        },
                    })

                    Promise.resolve()
                        .then(() => item.fn && item.fn())
                        .then(() => {
                            this.emitEvent({
                                name: 'availability-action-success',
                                payload: {
                                    action: item.name,
                                    message: this.getAvailabilityActionSuccessMessage(item.name),
                                    slotData: this.slotData,
                                },
                            })
                            this.emitEvent({ name: 'schedule-changed' })
                            this.emitEvent({ name: 'closeReferralContextMenu' })
                        })
                        .catch((error) => {
                            this.emitEvent({
                                name: 'availability-action-error',
                                payload: {
                                    action: item.name,
                                    message: this.getAvailabilityActionErrorMessage(error),
                                    slotData: this.slotData,
                                },
                            })
                        })
                        .finally(() => {
                            this.isLoadingAction = false
                            this.emitEvent({
                                name: 'availability-action-end',
                                payload: {
                                    slotData: this.slotData,
                                    action: item.name,
                                },
                            })
                        })

                    return
                }

                if (this.asContextMenu) {
                    const isProposeSlot = item.name === 'choose-slot' || item.name === 'propose-slot'
                    this.emitEvent({
                        name: item.name,
                        payload: {
                            ...this.referral,
                            ...(isProposeSlot && { referralId: this.referral.id }),
                            isReferringPhysician: this.isReferringPhysician,
                        },
                    })
                    this.emitEvent({ name: 'closeReferralContextMenu' })
                } else if (item.fn) {
                    let that = this
                    Promise.resolve()
                        .then(() => item.fn())
                        .then(() => {
                            that.emitEvent({ name: 'closeReferralContextMenu' })
                        })
                } else if (item.route) {
                    this.$router.push(item.route)
                }
            },

            setUnavailable() {
                return UsersAPI.setAvailabilitySlotUnavailable(this.slotData).then(() => {
                    this.$set(this.option.value, 'isUnavailable', true)
                    this.emitEvent({ name: 'schedule-changed' })
                })
            },

            createReferral() {
                if (this.$auth.getPractice().practiceType === PracticeTypeEnum.PHYSICIAN) {
                    const query = {
                        userId: this.slotData.userId,
                        practiceType: this.$auth.getPractice().practiceType,
                        searchBy: 'zipcode',
                        insurancePlanId: null,
                        specialityId: this.$auth.getActiveMember().specialities[0].speciality.id,
                        subSpecialityId: this.$auth.getActiveMember().specialities[0].subSpeciality.id,
                        physicianId: this.slotData.userId,
                        practiceId: this.slotData.practiceId,
                        practiceLocationId: this.slotData.practiceLocationId,
                        startTime: this.slotData.startTime,
                        endTime: this.slotData.endTime,
                        date: this.slotData.date,
                        selfReferred: true,
                    }

                    this.$router.push({
                        name: `referrals.new.make`,
                        query: query,
                    })
                } else {
                    const query = {
                        practiceLocationId: this.slotData.practiceLocationId,
                        practiceType: this.$auth.getPractice().practiceType,
                        practiceId: this.slotData.practiceId,
                        practiceName: this.$auth.getPractice().name,
                        startTime: this.slotData.startTime,
                        endTime: this.slotData.endTime,
                        date: this.slotData.date,
                        selfReferred: true,
                    }
                    this.$router.push({
                        name: `referrals.services.tests`,
                        query: query,
                    })
                }
            },

            setAvailable() {
                return UsersAPI.setAvailabilitySlotAvailable(this.slotData).then(() => {
                    this.$set(this.option.value, 'isUnavailable', false)
                    this.emitEvent({ name: 'schedule-changed' })
                })
            },

            getAvailabilityActionSuccessMessage(action) {
                if (action === 'set-unavailable') {
                    return this.$t('pages.availability.index.actions.success.unavailable')
                }

                if (action === 'set-available') {
                    return this.$t('pages.availability.index.actions.success.available')
                }

                return this.$t('pages.availability.index.actions.success.updated')
            },

            getAvailabilityActionErrorMessage(error) {
                return error?.response?.data?.message || error?.message || this.$t('pages.availability.index.actions.error')
            },

            shouldChooseSlot(referral) {
                return (
                    this.type &&
                    this.type === ReferralPathTypeEnum.MADE &&
                    referral &&
                    referral.sourceType === ReferralTrackingTypeEnum.MATCH &&
                    referral.status === ReferralStatusEnum.TIME_SLOTS_PROPOSED
                )
            },
            shouldProposeSlot(referral) {
                return (
                    this.type &&
                    this.type === ReferralPathTypeEnum.RECEIVED &&
                    referral &&
                    ((referral.sourceType === ReferralTrackingTypeEnum.MATCH && referral.status === ReferralStatusEnum.CREATED) ||
                        referral.status === ReferralStatusEnum.RESCHEDULE_REQUESTED_BY_PATIENT ||
                        referral.stageInfo?.stage === StagesEnum.PROPOSE_TIME_SLOTS ||
                        referral.stageInfo?.nextAction?.toLowerCase().includes('propose'))
                )
            },
            shouldExternalDecline(referral) {
                return (
                    this.type &&
                    this.type === ReferralPathTypeEnum.RECEIVED &&
                    referral &&
                    referral.sourceType === ReferralTrackingTypeEnum.MATCH &&
                    referral.status === ReferralStatusEnum.CREATED
                )
            },
            shouldUploadRequestedRecords(referral) {
                return (
                    this.type &&
                    this.type === ReferralPathTypeEnum.MADE &&
                    referral &&
                    referral.sourceType === ReferralTrackingTypeEnum.MATCH &&
                    referral.status === ReferralStatusEnum.CREATED
                )
            },
        },

        beforeMount() {
            this.option = cloneDeep(this.optionProp)
        },
    }
</script>
<style lang="css">
    .referral-context-menu__dropdown-menus {
        background-color: #ffffff !important;
        background: #ffffff !important;
        opacity: 1 !important;
        color: #1f2937;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
        z-index: 999;
    }

    .referral-context-menu__dropdown-menus.referral-actions {
        background-color: #ffffff !important;
        background: #ffffff !important;
        opacity: 1 !important;
    }

    .referral-context-menu__menu-item {
        color: #1f2937;
    }

    .referral-context-menu__menu-item .icon,
    .referral-context-menu__menu-item i {
        color: #4b5563;
    }

    .referral-context-menu__menu-item:hover .icon,
    .referral-context-menu__menu-item:hover i {
        color: #111827;
    }

    .referral-actions.is-loading {
        opacity: 0.88;
        pointer-events: none;
    }

    .disabled-item {
        opacity: 0.5;
        pointer-events: none;
        cursor: not-allowed;
    }

    .menu-item--loading {
        cursor: progress;
        opacity: 0.7;
    }
</style>
